import { createContext, useContext, useLayoutEffect, useRef, useState } from 'react'

const AutoHeightObservedElementContext = createContext<HTMLElement | null>(null)
const AutoHeightPaddingBottomContext = createContext<number>(0)

interface Props<T extends HTMLElement> {
  observedElement: T | null
  paddingBottom?: number
}

export function AutoHeightProvider<T extends HTMLElement>({
  children,
  paddingBottom = 0,
  observedElement
}: React.PropsWithChildren<Props<T>>) {
  return (
    <AutoHeightObservedElementContext.Provider value={observedElement}>
      <AutoHeightPaddingBottomContext.Provider value={paddingBottom}>{children}</AutoHeightPaddingBottomContext.Provider>
    </AutoHeightObservedElementContext.Provider>
  )
}

function useAutoHeightObservedElement() {
  return useContext(AutoHeightObservedElementContext)
}

function useAutoHeightPaddingBottom() {
  return useContext(AutoHeightPaddingBottomContext)
}

interface Dimensions {
  container: {
    y: number
    height: number
  }
  element: {
    y: number
  }
  paddingBottom: number
}

function calculateHeight(dimensions: Dimensions): number {
  // To calculate the height we need to know the following things.
  // - Container height
  // - Container y position (distance from start of browser window to top of HTML container element)
  // - Element y position (distance from start of browser window to top of HTML ref element)
  // - Padding between the bottom of the container element and ref element
  // To calculate the exact height we do the following:
  // container height - (element y - container y) - paddingBottom
  // The calculation where we subtract the container's y position from the element's y position
  // is needed to get the position of the element inside of the container.
  const elementYPositionInsideContainer = dimensions.element.y - dimensions.container.y

  return dimensions.container.height - elementYPositionInsideContainer - dimensions.paddingBottom
}

type AutoHeightOffsetRef<T> = React.RefObject<T>

type UseAutoHeight<T> = readonly [height: number, autoHeightOffsetRef: AutoHeightOffsetRef<T>]

export function useAutoHeight<T extends HTMLElement = HTMLElement>(): UseAutoHeight<T> {
  const elementRef = useRef<T>(null)
  const observedElement = useAutoHeightObservedElement()
  const paddingBottom = useAutoHeightPaddingBottom()

  const [dimensions, setDimensions] = useState<Dimensions>({
    container: {
      height: 0,
      y: 0
    },
    element: {
      y: 0
    },
    paddingBottom: 0
  })

  useLayoutEffect(() => {
    if (!observedElement) return

    // Set up subscription to an element higher up in the DOM.
    // We need to monitor this element for changes in size because that
    // would mean we probably need to change the height.
    const observer = new ResizeObserver(() => {
      const elementRect = elementRef.current?.getBoundingClientRect()
      const containerRect = observedElement.getBoundingClientRect()

      if (!elementRect) return

      if (
        dimensions.container.height !== containerRect.height ||
        dimensions.container.y !== containerRect.y ||
        dimensions.element.y !== elementRect.y ||
        dimensions.paddingBottom !== paddingBottom
      ) {
        setDimensions({
          container: { height: containerRect.height, y: containerRect.y },
          element: { y: elementRect.y },
          paddingBottom
        })
      }
    })

    observer.observe(observedElement)

    return () => observer.disconnect()
  }, [observedElement, paddingBottom, dimensions])

  return [calculateHeight(dimensions), elementRef]
}
